// The saved project strategy's retired label repair, moved out of the deleted
// project-strategy.js (old pipeline removal, wave 3c). An old job's saved
// strategy still loads and prices its costing sheet; nothing writes a new one.
import { isInternalStrategyScopeTitle } from '../services/client-scope-language';

const STRATEGY_LABEL_FILLER = new Set([
    'and', 'approach', 'at', 'for', 'in', 'local', 'of', 'on', 'repair',
    'repairs', 'system', 'targeted', 'the', 'to', 'works',
]);

function _strategyLabelToken(value) {
    const token = String(value || '').toLowerCase();
    const exact = {
        connected: 'connect', connecting: 'connect', connection: 'connect',
        installed: 'install', installing: 'install', installation: 'install',
        realigned: 'realign', realigning: 'realign', realignment: 'realign',
        reinstated: 'install', reinstating: 'install', reinstatement: 'install',
        rectification: 'rectify',
        remediated: 'remediate', remediating: 'remediate', remediation: 'remediate',
        removed: 'remove', removing: 'remove', removal: 'remove',
        repainted: 'repaint', repainting: 'repaint',
        replaced: 'replace', replacing: 'replace', replacement: 'replace',
        resealed: 'seal', resealing: 'seal', sealed: 'seal', sealing: 'seal',
        treated: 'treat', treating: 'treat', treatment: 'treat',
    };
    if (exact[token]) return exact[token];
    return token.length > 4 ? token.replace(/s$/, '') : token;
}

function _strategyLabelTokens(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .filter(token => !STRATEGY_LABEL_FILLER.has(token))
        .map(_strategyLabelToken);
}

function _isDiagnosisGroundedStrategyLabel(label, rows) {
    if (!label || isInternalStrategyScopeTitle(label)) return false;
    const labelTokens = [...new Set(_strategyLabelTokens(label))];
    if (labelTokens.length === 0) return false;
    const evidenceTokens = new Set(_strategyLabelTokens(rows.map(row => [
        row?.title,
        row?.action,
        row?.description,
        row?.addresses_mechanism,
        row?.addresses_root_cause,
    ].filter(Boolean).join(' ')).join(' ')));
    return labelTokens.every(token => evidenceTokens.has(token));
}

function _diagnosisDerivedStrategyLabel(rows) {
    const titles = [...new Set(rows
        .map(row => String(row?.title || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean))];
    if (titles.length === 0) return 'Physical repair plan';
    if (titles.length === 1) return titles[0];
    const lowerLead = value => value.charAt(0).toLowerCase() + value.slice(1);
    if (titles.length === 2) return `${titles[0]} and ${lowerLead(titles[1])}`;
    return `${titles[0]}, ${lowerLead(titles[1])}, and ${titles.length - 2} related repair steps`;
}

// The fixed label every diagnosis-anchored strategy got up to v5.65.35.
const RETIRED_DIAGNOSIS_ANCHORED_LABEL = 'Evidence-backed causal rectification';

/**
 * Repair the one retired generated label that v5.65.35 and earlier persisted
 * over an otherwise concrete diagnosis-anchored strategy. This is intentionally
 * immutable and applies only to generated, diagnosis-anchored records. A
 * builder-selected/non-anchored strategy is returned byte-for-byte unchanged.
 *
 * Any other label is returned unchanged: v5.65.36 and later grounded the label
 * against the full causal rows, while the stored steps keep only 80 characters
 * of each title, so re-judging a later label here would rewrite it.
 */
export function normaliseDiagnosisAnchoredStrategyLabel(strategy) {
    if (!strategy || typeof strategy !== 'object') return strategy;
    const chosen = strategy.chosen;
    if (!chosen || typeof chosen !== 'object' || chosen.diagnosis_anchored !== true) return strategy;
    if (String(chosen.label || '').trim() !== RETIRED_DIAGNOSIS_ANCHORED_LABEL) return strategy;
    const rows = Array.isArray(chosen.method_outline_steps) ? chosen.method_outline_steps : [];
    if (rows.length === 0) return strategy;
    if (_isDiagnosisGroundedStrategyLabel(chosen.label, rows)) return strategy;
    const chosenId = String(strategy.chosen_id || chosen.id || '').trim();
    const matchedAlternative = (Array.isArray(strategy.alternatives) ? strategy.alternatives : [])
        .find(alternative => String(alternative?.id || '').trim() === chosenId);
    const replacement = _isDiagnosisGroundedStrategyLabel(matchedAlternative?.label, rows)
        ? matchedAlternative.label
        : _diagnosisDerivedStrategyLabel(rows);
    if (!replacement || replacement === chosen.label) return strategy;
    return { ...strategy, chosen: { ...chosen, label: replacement } };
}
